import React, { useState, useMemo } from 'react';
import { 
  CreditCard, 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  Calendar,
  DollarSign,
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle,
  TrendingUp,
  TrendingDown,
  User,
  Building,
  Phone,
  Mail,
  Activity,
  ArrowLeft,
  BarChart3,
  FileText,
  Download
} from 'lucide-react';

// Mock payment tracking data
const payments = [
  {
    id: 'payment-001',
    payment_number: 'PAY-2025-001',
    invoice_id: 'invoice-001',
    invoice_number: 'INV-2025-001',
    customer_id: 'cust-001',
    customer_name: '台灣電子科技股份有限公司',
    customer_code: 'TETC-001',
    payment_date: '2025-01-25',
    due_date: '2025-02-20',
    amount: 3850000,
    paid_amount: 0,
    outstanding_amount: 3850000,
    currency: 'TWD',
    status: 'pending',
    payment_method: 'bank_transfer',
    payment_terms: '30天',
    created_date: '2025-01-21',
    follow_up_date: '2025-02-15',
    follow_up_count: 0,
    overdue_days: 0,
    contact_person: {
      name: '李經理',
      phone: '02-2345-6789',
      email: 'manager.li@tetc.com.tw'
    },
    payment_history: [
      {
        date: '2025-01-21',
        action: 'invoice_sent',
        description: '發票已發送給客戶',
        amount: 3850000
      }
    ],
    notes: '客戶信用良好，預期會準時付款',
    risk_level: 'low'
  },
  {
    id: 'payment-002',
    payment_number: 'PAY-2025-002',
    invoice_id: 'invoice-002',
    invoice_number: 'INV-2025-002',
    customer_id: 'cust-002',
    customer_name: '創新科技股份有限公司',
    customer_code: 'INNOV-002',
    payment_date: null,
    due_date: '2025-03-08',
    amount: 935000,
    paid_amount: 0,
    outstanding_amount: 935000,
    currency: 'TWD',
    status: 'pending',
    payment_method: 'bank_transfer',
    payment_terms: '45天',
    created_date: '2025-01-22',
    follow_up_date: '2025-03-01',
    follow_up_count: 0,
    overdue_days: 0,
    contact_person: {
      name: '張主任',
      phone: '03-5678-901',
      email: 'director.zhang@innov.com.tw'
    },
    payment_history: [
      {
        date: '2025-01-22',
        action: 'invoice_created',
        description: '發票已建立，待發送',
        amount: 935000
      }
    ],
    notes: '新客戶，需要密切關注付款狀況',
    risk_level: 'medium'
  }
];

const PaymentTracking = ({ onBack, navigationSource }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  const formatCurrency = (amount, currency = 'TWD') => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'partial': 'bg-orange-100 text-orange-800',
      'paid': 'bg-green-100 text-green-800',
      'overdue': 'bg-red-100 text-red-800',
      'cancelled': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': '待收款',
      'partial': '部分收款',
      'paid': '已收款',
      'overdue': '逾期',
      'cancelled': '已取消'
    };
    return labels[status] || status;
  };

  const getRiskColor = (level) => {
    const colors = {
      'low': 'bg-green-100 text-green-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'high': 'bg-red-100 text-red-800'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  const getRiskLabel = (level) => {
    const labels = {
      'low': '低風險',
      'medium': '中風險',
      'high': '高風險'
    };
    return labels[level] || level;
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      'bank_transfer': '銀行轉帳',
      'cash': '現金',
      'check': '支票',
      'credit_card': '信用卡',
      'wire_transfer': '電匯'
    };
    return labels[method] || method;
  };

  const filteredPayments = useMemo(() => {
    return payments.filter(payment => {
      if (statusFilter !== 'all' && payment.status !== statusFilter) {
        return false;
      }
      if (riskFilter !== 'all' && payment.risk_level !== riskFilter) {
        return false;
      }
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          payment.payment_number.toLowerCase().includes(searchLower) ||
          payment.invoice_number.toLowerCase().includes(searchLower) ||
          payment.customer_name.toLowerCase().includes(searchLower) ||
          payment.customer_code.toLowerCase().includes(searchLower)
        );
      }
      return true;
    });
  }, [statusFilter, riskFilter, searchTerm]);

  const renderWorkflowBanner = () => {
    if (navigationSource === 'sales-workflow') {
      return (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-l-4 border-purple-500 p-4 mb-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-purple-500 p-2 rounded-full">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-purple-800">來自業務流程: 銷售週期管理</h3>
                <p className="text-sm text-purple-600">階段E: 發票開立與款項收取 - 收款追蹤</p>
              </div>
            </div>
            <button
              onClick={onBack}
              className="flex items-center space-x-2 bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>返回銷售週期</span>
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderDashboard = () => {
    const totalPayments = payments.length;
    const pendingPayments = payments.filter(p => p.status === 'pending').length;
    const paidPayments = payments.filter(p => p.status === 'paid').length;
    const overduePayments = payments.filter(p => p.status === 'overdue').length;
    const totalOutstanding = payments.reduce((sum, p) => sum + p.outstanding_amount, 0);
    const totalReceived = payments.reduce((sum, p) => sum + p.paid_amount, 0);

    return (
      <div className="space-y-6">
        {renderWorkflowBanner()}
        
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">收款追蹤儀表板</h2>
          <button
            onClick={() => setActiveView('create')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>新增收款記錄</span>
          </button>
        </div>

        {/* 統計卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">收款總數</p>
                <p className="text-2xl font-bold text-gray-800">{totalPayments}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <CreditCard className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">待收款</p>
                <p className="text-2xl font-bold text-gray-800">{pendingPayments}</p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-full">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">應收總額</p>
                <p className="text-2xl font-bold text-gray-800">{formatCurrency(totalOutstanding)}</p>
              </div>
              <div className="bg-orange-100 p-3 rounded-full">
                <DollarSign className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">已收總額</p>
                <p className="text-2xl font-bold text-gray-800">{formatCurrency(totalReceived)}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        {/* 收款狀態分佈 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">收款狀態分佈</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">待收款</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-yellow-600 h-2 rounded-full" style={{ width: '100%' }}></div>
                  </div>
                  <span className="text-sm font-medium">{pendingPayments}</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">部分收款</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-orange-600 h-2 rounded-full" style={{ width: '0%' }}></div>
                  </div>
                  <span className="text-sm font-medium">0</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">已收款</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{ width: '0%' }}></div>
                  </div>
                  <span className="text-sm font-medium">0</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">逾期</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-red-600 h-2 rounded-full" style={{ width: '0%' }}></div>
                  </div>
                  <span className="text-sm font-medium">0</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">風險等級分佈</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">低風險</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{ width: '50%' }}></div>
                  </div>
                  <span className="text-sm font-medium">1</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">中風險</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-yellow-600 h-2 rounded-full" style={{ width: '50%' }}></div>
                  </div>
                  <span className="text-sm font-medium">1</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">高風險</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-red-600 h-2 rounded-full" style={{ width: '0%' }}></div>
                  </div>
                  <span className="text-sm font-medium">0</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 最新收款記錄 */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">最新收款記錄</h3>
              <button
                onClick={() => setActiveView('list')}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                查看全部
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {payments.map((payment) => (
                <div
                  key={payment.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    setSelectedPayment(payment);
                    setActiveView('detail');
                  }}
                >
                  <div className="flex items-center space-x-4">
                    <div className="bg-blue-100 p-3 rounded-full">
                      <CreditCard className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">{payment.customer_name}</h4>
                      <p className="text-sm text-gray-600">{payment.payment_number} - {payment.invoice_number}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="font-semibold text-gray-800">應收: {formatCurrency(payment.outstanding_amount)}</p>
                      <p className="text-sm text-gray-600">到期日: {payment.due_date}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(payment.risk_level)}`}>
                      {getRiskLabel(payment.risk_level)}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                      {getStatusLabel(payment.status)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderList = () => (
    <div className="space-y-6">
      {renderWorkflowBanner()}
      
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">收款追蹤列表</h2>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setActiveView('create')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>新增收款記錄</span>
          </button>
          <button
            onClick={() => setActiveView('dashboard')}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回儀表板</span>
          </button>
        </div>
      </div>

      {/* 搜尋和篩選 */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="搜尋收款編號或客戶..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有狀態</option>
            <option value="pending">待收款</option>
            <option value="partial">部分收款</option>
            <option value="paid">已收款</option>
            <option value="overdue">逾期</option>
            <option value="cancelled">已取消</option>
          </select>
          
          <select
            value={riskFilter}
            onChange={(e) => setRiskFilter(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有風險等級</option>
            <option value="low">低風險</option>
            <option value="medium">中風險</option>
            <option value="high">高風險</option>
          </select>
        </div>
      </div>

      {/* 收款表格 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                收款資訊
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                客戶
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                到期日期
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                應收金額
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                風險等級
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                狀態
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredPayments.map((payment) => (
              <tr key={payment.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="bg-blue-100 p-2 rounded-full mr-3">
                      <CreditCard className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{payment.payment_number}</div>
                      <div className="text-sm text-gray-500">{payment.invoice_number}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{payment.customer_name}</div>
                  <div className="text-sm text-gray-500">{payment.customer_code}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{payment.due_date}</div>
                  <div className="text-sm text-gray-500">{payment.payment_terms}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(payment.outstanding_amount)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(payment.risk_level)}`}>
                    {getRiskLabel(payment.risk_level)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                    {getStatusLabel(payment.status)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => {
                      setSelectedPayment(payment);
                      setActiveView('detail');
                    }}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDetail = () => {
    if (!selectedPayment) return null;

    return (
      <div className="space-y-6">
        {renderWorkflowBanner()}
        
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">收款詳細資訊</h2>
          <div className="flex items-center space-x-4">
            <button className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
              <Plus className="w-4 h-4" />
              <span>記錄付款</span>
            </button>
            <button className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
              <Phone className="w-4 h-4" />
              <span>催收提醒</span>
            </button>
            <button
              onClick={() => setActiveView('list')}
              className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>返回列表</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 基本資訊 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">基本資訊</h3>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">收款編號:</span>
                <span className="font-medium">{selectedPayment.payment_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">發票編號:</span>
                <span className="font-medium">{selectedPayment.invoice_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">建立日期:</span>
                <span className="font-medium">{selectedPayment.created_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">到期日期:</span>
                <span className="font-medium">{selectedPayment.due_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">付款條件:</span>
                <span className="font-medium">{selectedPayment.payment_terms}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">付款方式:</span>
                <span className="font-medium">{getPaymentMethodLabel(selectedPayment.payment_method)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">收款狀態:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedPayment.status)}`}>
                  {getStatusLabel(selectedPayment.status)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">風險等級:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(selectedPayment.risk_level)}`}>
                  {getRiskLabel(selectedPayment.risk_level)}
                </span>
              </div>
            </div>
          </div>

          {/* 客戶資訊 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">客戶資訊</h3>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">客戶名稱:</span>
                <span className="font-medium">{selectedPayment.customer_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">客戶代碼:</span>
                <span className="font-medium">{selectedPayment.customer_code}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">聯絡人:</span>
                <span className="font-medium">{selectedPayment.contact_person.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">聯絡電話:</span>
                <span className="font-medium">{selectedPayment.contact_person.phone}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">電子郵件:</span>
                <span className="font-medium">{selectedPayment.contact_person.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">追蹤次數:</span>
                <span className="font-medium">{selectedPayment.follow_up_count} 次</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">下次追蹤:</span>
                <span className="font-medium">{selectedPayment.follow_up_date}</span>
              </div>
            </div>
          </div>
        </div>

        {/* 金額資訊 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">金額資訊</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">發票總額</p>
              <p className="text-2xl font-bold text-gray-800">{formatCurrency(selectedPayment.amount)}</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600">已收金額</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(selectedPayment.paid_amount)}</p>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <p className="text-sm text-gray-600">未收金額</p>
              <p className="text-2xl font-bold text-orange-600">{formatCurrency(selectedPayment.outstanding_amount)}</p>
            </div>
          </div>
        </div>

        {/* 付款歷史 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">付款歷史</h3>
          <div className="space-y-4">
            {selectedPayment.payment_history.map((history, index) => (
              <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 p-2 rounded-full">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-800">{history.description}</h4>
                    <p className="text-sm text-gray-600">{history.date}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-800">{formatCurrency(history.amount)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 備註 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">備註</h3>
          <p className="text-gray-600 bg-gray-50 p-3 rounded">{selectedPayment.notes}</p>
        </div>

        {/* 催收提醒 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">催收提醒</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-center space-x-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                <span className="font-medium text-yellow-800">付款提醒</span>
              </div>
              <p className="text-sm text-yellow-700">
                距離到期日還有 {Math.ceil((new Date(selectedPayment.due_date) - new Date()) / (1000 * 60 * 60 * 24))} 天
              </p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center space-x-2 mb-2">
                <Clock className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-800">下次追蹤</span>
              </div>
              <p className="text-sm text-blue-700">
                預定追蹤日期: {selectedPayment.follow_up_date}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return renderDashboard();
      case 'list':
        return renderList();
      case 'detail':
        return renderDetail();
      case 'create':
        return (
          <div className="space-y-6">
            {renderWorkflowBanner()}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">新增收款記錄</h2>
              <p className="text-gray-600">收款記錄新增功能開發中...</p>
              <button
                onClick={() => setActiveView('dashboard')}
                className="mt-4 flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>返回儀表板</span>
              </button>
            </div>
          </div>
        );
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {renderContent()}
      </div>
    </div>
  );
};

export default PaymentTracking; 